/**
 * Site-Wide GA4 Analytics Utility
 *
 * Singleton analytics class that sends custom events to GA4 via the
 * dataLayer / gtag pattern already used by quiz-analytics.js.
 * Loaded site-wide from theme.liquid so it is available on every page.
 *
 * ─────────────────────────────────────────────────────────────────────
 * QUICK START
 * ─────────────────────────────────────────────────────────────────────
 *
 * After a successful add-to-cart action, call:
 *
 *   window.SiteAnalytics.trackAddToCart({
 *     item_name:        'The Razor',
 *     item_variant:     'Rose Gold',
 *     source_component: 'pdp_main',
 *     price:            9.00,
 *     position:         null
 *   });
 *
 * `page_path` and `session_id` are appended automatically.
 *
 * ─────────────────────────────────────────────────────────────────────
 * DATA-ATTRIBUTE CONVENTION  (for Liquid templates)
 * ─────────────────────────────────────────────────────────────────────
 *
 * Any element that triggers an add-to-cart can carry these attributes
 * so that event data can be read from the DOM at fire-time:
 *
 *   data-track-atc              — Marker: element participates in ATC tracking
 *   data-track-item-name        — Product title   (e.g. "The Razor")
 *   data-track-item-variant     — Variant title   (e.g. "Rose Gold")
 *   data-track-source           — Source component (e.g. "pdp_main")
 *   data-track-price            — Numeric price   (e.g. "9.00")
 *   data-track-position         — 1-based index   (e.g. "2"), optional
 *   data-track-selling-plan-id  — Subscription selling plan ID, (e.g. "plan_123"), optional
 *
 * Helper: SiteAnalytics.trackAddToCartFromElement(element) reads these
 * attributes and fires the event in one call.
 *
 * ─────────────────────────────────────────────────────────────────────
 * INTEGRATION EXAMPLES
 * ─────────────────────────────────────────────────────────────────────
 *
 * 1) PDP Main Button  (snippets/pdp-product-info.liquid)
 *    ────────────────────────────────────────────────────
 *    The product-form custom element dispatches `variant:added` on
 *    document.documentElement after a successful fetch to /cart/add.js.
 *
 *    Liquid — add data attributes to the ATC <button>:
 *
 *      <button type="submit"
 *        class="pdp-buy-button__btn pdp-buy-button__btn--atc"
 *        data-track-atc
 *        data-track-item-name="{{ product.title }}"
 *        data-track-item-variant="{{ variantToSelect.title }}"
 *        data-track-source="pdp_main"
 *        data-track-price="{{ variantToSelect.price | divided_by: 100.0 }}">
 *
 *    JS — call after the cart add succeeds:
 *
 *      window.SiteAnalytics.trackAddToCart({
 *        item_name:        '{{ product.title }}',
 *        item_variant:     selectedVariant.title,
 *        source_component: 'pdp_main',
 *        price:            selectedVariant.price / 100,
 *        position:         null
 *      });
 *
 *    Or use the DOM-based helper:
 *
 *      window.SiteAnalytics.trackAddToCartFromElement(buttonEl);
 *
 * 2) PCP Card  (snippets/athena-pcp-card.liquid)
 *    ────────────────────────────────────────────
 *    Same product-form mechanism. Position comes from the collection
 *    grid's loop index.
 *
 *    Liquid:
 *
 *      <button type="submit"
 *        class="athena-pcp-card__quick-add-btn"
 *        data-track-atc
 *        data-track-item-name="{{ product.title }}"
 *        data-track-item-variant="{{ first_variant.title }}"
 *        data-track-source="pcp_card"
 *        data-track-price="{{ subscription_price | divided_by: 100.0 }}"
 *        data-track-position="{{ card_index }}">
 *
 *    When variant swatches change, the AthenaPcpCard JS class should
 *    also update data-track-item-variant and data-track-price on the
 *    submit button so the values stay current.
 *
 * 3) PDP Cross-Sell Card  (sections/pdp-steps-cross-sell.liquid
 *                          → snippets/athena-cross-sell-card.liquid)
 *    ────────────────────────────────────────────────────────────
 *    Liquid — pass `position` into the snippet from the section loop:
 *
 *      {%- render 'athena-cross-sell-card',
 *            product: block.settings.product,
 *            card_position: forloop.index -%}
 *
 *    Inside the snippet, add data attrs to the ATC button:
 *
 *      <button type="submit"
 *        class="athena-cross-sell-card__atc-btn"
 *        data-track-atc
 *        data-track-item-name="{{ product.title }}"
 *        data-track-item-variant="{{ first_variant.title }}"
 *        data-track-source="pdp_cross_sell_card"
 *        data-track-price="{{ subscription_price | divided_by: 100.0 }}"
 *        data-track-position="{{ card_position }}">
 *
 * 4) Mini Cart Cross-Sell  (sections/mini-cart.liquid — CrossSellSingle)
 *    ──────────────────────────────────────────────────────────────────
 *    This component does NOT use the product-form custom element; it
 *    calls fetch('/cart/add.js') directly inside CrossSellSingle.
 *    Tracking must be added in the addToCart() success path:
 *
 *      // Inside CrossSellSingle.addToCart(), after a successful response:
 *      if (window.SiteAnalytics) {
 *        const titleEl = this.querySelector('.cross-sell-single__title');
 *        const priceEl = this.querySelector('.cross-sell-single__price');
 *        window.SiteAnalytics.trackAddToCart({
 *          item_name:        titleEl?.textContent?.trim() || '',
 *          item_variant:     this.currentVariantTitle || '',
 *          source_component: 'cart_cross_sell_card',
 *          price:            parseFloat(priceEl?.textContent?.replace(/[^0-9.]/g, '')) || 0,
 *          position:         1
 *        });
 *      }
 *
 * ─────────────────────────────────────────────────────────────────────
 * DELEGATED LISTENER PATTERN  (optional, covers product-form surfaces)
 * ─────────────────────────────────────────────────────────────────────
 *
 * Instead of (or in addition to) calling trackAddToCart manually in
 * each component, you can rely on the automatic listener that this
 * file binds to the `variant:added` event on document.documentElement.
 *
 * When a product-form fires `variant:added`, SiteAnalytics will walk
 * up the DOM from the form to find the nearest element with
 * `data-track-atc`. If found, it reads the data-track-* attributes
 * and fires the event automatically. No extra JS is needed in that
 * component — only the Liquid data attributes.
 *
 * This covers: PDP main, PCP cards, and cross-sell cards.
 * It does NOT cover the mini cart cross-sell (CrossSellSingle), which
 * must call trackAddToCart explicitly (see example 4 above).
 */

if (typeof SiteAnalytics === 'undefined') {
  const SESSION_STORAGE_KEY = '_ac_site_session_id';

  class SiteAnalytics {
    constructor() {
      this.sessionId = this.getOrCreateSessionId();
      this.initializeGA4();
      this.bindGlobalListeners();
    }

    /**
     * Initialises gtag.js directly for GA4 property G-Z5BWJ1WW16,
     * bypassing GTM for custom events. Loads the gtag.js script once
     * if it hasn't already been added to the page.
     */
    initializeGA4() {
      window.dataLayer = window.dataLayer || [];
      window.gtag = window.gtag || function () {
        dataLayer.push(arguments);
      };

      // Load gtag.js if not already on the page
      if (!document.querySelector('script[src*="googletagmanager.com/gtag/js"]')) {
        const script = document.createElement('script');
        script.async = true;
        script.src = 'https://www.googletagmanager.com/gtag/js?id=G-Z5BWJ1WW16';
        document.head.appendChild(script);
      }

      if (!window._acGA4Configured) {
        window._acGA4Configured = true;
        gtag('js', new Date());
        gtag('config', 'G-Z5BWJ1WW16');
      }
    }

    /**
     * Returns an existing session ID from sessionStorage or generates
     * a new one. The ID persists for the lifetime of the browser tab
     * (cleared when the tab/window is closed), which aligns with GA4's
     * definition of a session.
     *
     * @returns {string} e.g. "site_1710850000000_k8f3md2qx"
     */
    getOrCreateSessionId() {
      try {
        const existing = sessionStorage.getItem(SESSION_STORAGE_KEY);
        if (existing) return existing;

        const id = `site_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
        sessionStorage.setItem(SESSION_STORAGE_KEY, id);
        return id;
      } catch (_e) {
        return `site_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      }
    }

    // ───────────────────────────── Events ─────────────────────────────

    /**
     * Fires the `ac_add_to_cart` event to GA4.
     *
     * @param {Object}      params
     * @param {string}      params.item_name        — Product title
     * @param {string}      params.item_variant      — Variant title
     * @param {string}      params.source_component  — Where the add happened
     *   Accepted values: "pdp_main" | "pcp_card" | "pdp_cross_sell_card"
     *                  | "cart_cross_sell_card" | <any future identifier>
     * @param {number|string} params.price           — Unit price (numeric)
     * @param {number|null}   [params.position=null] — 1-based index in a
     *   list/carousel, or null when not applicable
     * @param {boolean}       [params.oos=false]     — true when the waitlist
     *   popup was shown instead of a real cart add
     * @param {string|number|null} [params.selling_plan_id=null] — Subscription
     *   selling plan ID when the add was a subscription purchase, or null for
     *   a one-time purchase.
     * @param {string|null} [params.frequency_cadence=null] — Razor kit frequency
     *   selector cadence (e.g. "every_2_months", "annual"). Passed via ...extra
     *   from frequency-selector.js; register as a GA4 custom dimension.
     */
    trackAddToCart({
      item_id,
      item_name,
      item_variant,
      source_component,
      price,
      position = null,
      oos = false,
      selling_plan_id = null,
      ...extra
    }) {
      const parsedPrice = parseFloat(price) || 0;
      const sellingPlanId =
        selling_plan_id != null && String(selling_plan_id).trim() !== ''
          ? selling_plan_id
          : null;

      const eventData = {
        item_currency: window.Shopify?.currency?.active || 'USD',
        value: parsedPrice,
        item_id: item_id || '',
        item_name: item_name || '',
        item_variant: item_variant || '',
        price: parsedPrice,
        quantity: 1,
        source_component: source_component || '',
        page_path: window.location.pathname,
        ...(position != null && { position: parseInt(position, 10) }),
        oos: Boolean(oos),
        selling_plan_id: sellingPlanId,
        ac_session_id: this.sessionId,  // session_id is a reserved keyword in GA4
        ...extra
      };

      // Shipping price A/B test group ('a' | 'b' | 'control'), from snippets/shipping-test-group.liquid.
      // Only sent for shoppers in the test.
      const shippingGroup = window.acShippingGroup?.get?.();
      if (shippingGroup) eventData.shipping_group = shippingGroup;

      gtag('event', 'ac_add_to_cart', eventData);
    }

    /**
     * Reads `data-track-*` attributes from an element (or its closest
     * ancestor with `[data-track-atc]`) and fires trackAddToCart.
     *
     * Useful when the Liquid template stamps all tracking data onto the
     * button/form so no extra JS variables are needed.
     *
     * @param {HTMLElement} element — The clicked button/form or a child of it
     * @returns {boolean} true if the event was fired, false if no tracking
     *   attributes were found
     */
    trackAddToCartFromElement(element) {
      const tracked = element?.closest?.('[data-track-atc]') || element;
      if (!tracked || !tracked.dataset) return false;

      const d = tracked.dataset;
      if (!d.trackItemName && !d.trackSource) return false;

      const form =
        (tracked.closest && tracked.closest('form')) ||
        (tracked.querySelector && tracked.querySelector('form'));
      const sellingPlanFromForm =
        form?.querySelector?.('input[name="selling_plan"]')?.value;
      const sellingPlanId = sellingPlanFromForm || d.trackSellingPlanId || null;

      this.trackAddToCart({
        item_id:          d.trackItemId,
        item_name:        d.trackItemName,
        item_variant:     d.trackItemVariant,
        source_component: d.trackSource,
        price:            d.trackPrice,
        position:         d.trackPosition,
        oos:              d.trackOos === 'true',
        selling_plan_id:  sellingPlanId,
        ...(d.trackQuizId && { quiz_id: d.trackQuizId })
      });

      return true;
    }

    // ──────────────────── Global delegated listeners ──────────────────

    /**
     * Listens for the `variant:added` custom event that the product-form
     * custom element dispatches on document.documentElement after a
     * successful cart add. If the originating form (or its submit button)
     * carries `data-track-atc`, the event fires automatically.
     *
     * This covers PDP, PCP card, and cross-sell card surfaces without
     * any additional JS in those components — only the Liquid data
     * attributes are needed.
     */
    bindGlobalListeners() {
      document.documentElement.addEventListener('variant:added', (e) => {
        // Walk composedPath() to find the originating <form is="product-form">
        // element. This is more reliable than e.target.closest('form') because
        // some dispatch sites (e.g. section--routines.js) call this.dispatchEvent
        // where `this` may be window rather than the form element, causing
        // e.target to be document.documentElement and querySelector to return
        // the first [data-track-atc] on the page instead of the correct one.
        const path = e.composedPath ? e.composedPath() : [];
        const form = path.find(el => el instanceof Element && el.matches('form'))
          || e.target?.closest?.('form')
          || e.target;

        if (!form) return;

        const trackableEl =
          form.querySelector('[data-track-atc]') ||
          form.closest('[data-track-atc]');

        if (trackableEl) {
          this.trackAddToCartFromElement(trackableEl);
        }
      });
    }
  }

  window.SiteAnalytics = new SiteAnalytics();
}
